'use client';

import {createStore, type StoreApi} from '@lib/create-store';
import {getStorage} from '@stores/storage';
import {createContext, useContext} from 'react';
import {shallow} from 'zustand/shallow';
import {useStoreWithEqualityFn} from 'zustand/traditional';

type ApplicationMetadataStore = {
  customLogos: Map<string, string>;
  isLoading: boolean;

  setCustomLogo: (applicationKey: string, logoUrl: string) => void;
  setCustomLogos: (logos: Array<{application_id: string; url: string}>) => void;
  setIsLoading: (isLoading: boolean) => void;
  removeCustomLogo: (applicationKey: string) => void;
  getCustomLogo: (applicationKey: string) => string | undefined;
  clearAllCustomLogos: () => void;
};

export type ApplicationMetadataStoreApi = StoreApi<ApplicationMetadataStore>;

const storage = getStorage<ApplicationMetadataStore>();

/**
 * Normalize application key to handle formats like 'pl_applications/abc123'
 * Extracts the ID part after the last '/' if present
 */
export const normalizeApplicationKey = (applicationKey: string): string => {
  if (applicationKey.includes('/')) {
    return applicationKey.split('/').pop() ?? applicationKey;
  }
  return applicationKey;
};

export const createApplicationMetadataStore = () => {
  return createStore<ApplicationMetadataStore>(
    (set, get) => ({
      customLogos: new Map(),
      isLoading: false,

      setCustomLogo: (applicationKey: string, logoUrl: string) => {
        const normalizedKey = normalizeApplicationKey(applicationKey);
        set(state => {
          // Create new Map to ensure reference changes and subscribers are notified
          const newMap = new Map(state.customLogos);
          newMap.set(normalizedKey, logoUrl);
          state.customLogos = newMap;
        });
      },

      setCustomLogos: (logos: Array<{application_id: string; url: string}>) => {
        const newMap = new Map<string, string>();
        logos.forEach(logo => {
          const normalizedKey = normalizeApplicationKey(logo.application_id);
          newMap.set(normalizedKey, logo.url);
        });
        set({customLogos: newMap});
      },

      setIsLoading: (loading: boolean) => {
        set({isLoading: loading});
      },

      removeCustomLogo: (applicationKey: string) => {
        const normalizedKey = normalizeApplicationKey(applicationKey);
        set(state => {
          // Create new Map to ensure reference changes and subscribers are notified
          const newMap = new Map(state.customLogos);
          newMap.delete(normalizedKey);
          state.customLogos = newMap;
        });
      },

      getCustomLogo: (applicationKey: string) => {
        const normalizedKey = normalizeApplicationKey(applicationKey);
        return get().customLogos.get(normalizedKey);
      },

      clearAllCustomLogos: () => {
        set(() => ({
          customLogos: new Map(),
        }));
      },
    }),
    {
      name: 'ApplicationMetadataStore',
      version: 1,
      persist: {
        storage,
      },
    },
  );
};

export const useApplicationMetadataStore = <U>(
  selector: (state: ApplicationMetadataStore) => U,
  equalityFn: (left: U, right: U) => boolean = shallow,
): U => {
  const context = useContext(ApplicationMetadataContext);
  if (!context) {
    throw new Error('useApplicationMetadataStore must be used within an ApplicationMetadataProvider');
  }
  return useStoreWithEqualityFn(context, selector, equalityFn);
};

// selectors
export const useGetCustomLogoUrl = (applicationKey: string) => {
  return useApplicationMetadataStore(state => state.getCustomLogo(applicationKey));
};

export const ApplicationMetadataContext = createContext<ReturnType<typeof createApplicationMetadataStore> | null>(null);
